import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const clampPct = (v) => Math.max(0, Math.min(100, v ?? 0));
const fmtCompact = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '-';
  try {
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(v));
  } catch {
    return Number(v).toLocaleString();
  }
};

function pickPercents(d = {}) {
  const lc = num(d.long_traders_count);
  const sc = num(d.short_traders_count);
  if (lc != null && sc != null && lc + sc > 0) {
    const lp = Math.round((lc / (lc + sc)) * 100);
    return [clampPct(lp), clampPct(100 - lp)];
  }
  const lp = num(d.long_traders_percent);
  const sp = num(d.short_traders_percent);
  if (lp != null && sp != null && lp + sp > 0) return [clampPct(lp), clampPct(sp)];
  const llp = num(d.long_lots_percent), slp = num(d.short_lots_percent);
  if (llp != null && slp != null && llp + slp > 0) return [clampPct(llp), clampPct(slp)];
  const llc = num(d.long_lots_count), slc = num(d.short_lots_count);
  if (llc != null && slc != null && llc + slc > 0) {
    const p = Math.round((llc / (llc + slc)) * 100);
    return [clampPct(p), clampPct(100 - p)];
  }
  return [0, 0];
}

const TrendItem = ({ data, isOnsaMarket, size = 'md' }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const dashboardWeb = isDesktop && !isOnsaMarket;

  const { pair, long_traders_count, short_traders_count } = data || {};
  const [longPct, shortPct] = pickPercents(data);
  const tall = size === 'lg';

  const textBase = {
    fontSize: tall ? 12 : 11,
    lineHeight: tall ? '16px' : '14px',
    color: '#1C1C1C',
    whiteSpace: dashboardWeb ? 'normal' : 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid #e0e0e0',
        bgcolor: '#f7f7f7',
        borderRadius: 3,
        p: tall ? 1.5 : 1,
        minHeight: tall ? 116 : 'auto',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1,
          py: tall ? 0.75 : 0.5,
          fontSize: tall ? 12 : 11,
          fontWeight: 600,
          color: '#1C1C1C',
          bgcolor: '#E0E0E0',
          borderRadius: 9999,
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={pair}
      >
        {pair}
      </Box>

      {dashboardWeb ? (
        <Box
          sx={{
            mt: tall ? 1.25 : 1,
            px: 0.5,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            columnGap: 0.5,
            rowGap: 0.25,
            alignItems: 'center',
          }}
        >
          <Typography sx={{ ...textBase, fontWeight: 600 }}>{longPct}%</Typography>
          <Typography sx={{ ...textBase, fontWeight: 600, textAlign: 'right' }}>{shortPct}%</Typography>
          <Typography sx={textBase}>{fmtCompact(long_traders_count)} Traders</Typography>
          <Typography sx={{ ...textBase, textAlign: 'right' }}>{fmtCompact(short_traders_count)} Traders</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: tall ? 1.25 : 1,
            px: 0.5,
            gap: 1,
          }}
        >
          <Typography sx={textBase}>
            {longPct}% - {fmtCompact(long_traders_count)} Traders
          </Typography>
          <Typography sx={textBase}>
            {fmtCompact(short_traders_count)} Traders - {shortPct}%
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: tall ? 1.25 : 1 }}>
        <Box flex={longPct} height={tall ? 16 : 10} borderRadius={9999} bgcolor="#92A9F1" />
        <Box flex={shortPct} height={tall ? 16 : 10} borderRadius={9999} bgcolor="#FFA5A6" />
      </Box>
    </Box>
  );
};

export default TrendItem;
